import type { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Logo } from '../../ui';

interface AuthLayoutProps {
  children: ReactNode;
  title: string;
  subtitle: string;
}

export function AuthLayout({ children, title, subtitle }: AuthLayoutProps) {
  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col">
      {/* Header */}
      <header className="relative z-10 px-6 py-6 sm:px-8">
        <div className="mx-auto max-w-md">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link to="/" className="inline-block">
              <Logo variant="wordmark" size="md" />
            </Link>
          </motion.div>
        </div>
      </header>

      {/* Main content */}
      <main className="relative z-10 flex-1 flex items-center justify-center px-6 pb-12 sm:px-8">
        <div className="w-full max-w-md">
          <motion.div
            className="text-center mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <h1 className="font-display text-3xl sm:text-4xl font-semibold text-surface-900 dark:text-white">
              {title}
            </h1>
            <p className="mt-2 text-surface-600 dark:text-surface-400">
              {subtitle}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {children}
          </motion.div>
        </div>
      </main>

      {/* Crisis helpline footer */}
      <footer className="relative z-10 bg-surface-50/80 dark:bg-surface-900/80 backdrop-blur-sm border-t border-surface-200 dark:border-surface-700 px-6 py-3">
        <div className="mx-auto max-w-md text-center">
          <p className="text-xs text-surface-500 dark:text-surface-400">
            In crisis?{' '}
            <a href="tel:988" className="font-medium text-accent-600 dark:text-accent-400 hover:underline">
              Call 988
            </a>{' '}
            (Suicide & Crisis Lifeline)
          </p>
        </div>
      </footer>
    </div>
  );
}
